import ListLoader from './ListLoader.js';
import { uniqueValues, parseOrderList, valueCount } from './listFacets.js';

/**
 * SidebarFacets
 * 記事詳細ページ（ノウハウ/事例＝ListView無し）のサイドバー「カテゴリー一覧」「タグ一覧」を JSON から生成する。
 * カテゴリ・タグとも MT では出さず JS で出す（一覧ページと同一挙動に統一）。
 *
 * - データは ListLoader.load()（basePath|type|years でキャッシュ）経由＝1ページ内で複数呼んでも fetch は1回。
 * - リンクは一覧ページ＋絞り込みパラメータ（例 /blog/?category=X#list, /blog/?tags=WEB広告#list）＝クリックで絞り込み済み一覧へ遷移。
 * - 対象＝.l-archive の外にある .p-sidebar-cats / .p-sidebar-tags（一覧ページ内は ListView が担当＝二重処理しない）。
 * - base/type は data 属性（任意）優先、無ければ URL 先頭セグメントから導出（/blog/… → base=/blog/・type=blog）。
 */
export default class SidebarFacets {
  constructor() {
    document.querySelectorAll('.p-sidebar-cats[data-sidebar-cats]').forEach((el) => {
      if (!el.closest('.l-archive')) this.setupCats(el);
    });
    document.querySelectorAll('.p-sidebar-tags[data-sidebar-tags]').forEach((el) => {
      if (!el.closest('.l-archive')) this.setupTags(el);
    });
  }

  // カテゴリー一覧（単一・件数付き）。詳細では絞り込み不可なので、一覧の ?category= へのリンクとして生成（先頭に「すべて」）
  async setupCats(el) {
    const field = el.dataset.sidebarCats || 'category';
    const { base, type } = this.resolve(el);
    if (!type) return;
    const items = await ListLoader.load({ basePath: base, type });
    const values = uniqueValues(items, field, { sort: true });
    if (values.length === 0) return; // 空なら静的フォールバックのまま
    const item = (val, label, count) => `<a href="${base}${val ? `?${encodeURIComponent(field)}=${encodeURIComponent(val)}#list` : ''}"><span class="label">${this.escape(label)}</span><span class="count">(${count})</span></a>`;
    el.innerHTML = item('', 'すべて', items.length)
      + values.map((v) => item(v, v, valueCount(items, field, v))).join('');
  }

  // タグ一覧（複数・五十音順）。data-sidebar-tags-order で先頭固定可
  async setupTags(el) {
    const field = el.dataset.sidebarTags || 'tags';
    const { base, type } = this.resolve(el);
    if (!type) return;
    const items = await ListLoader.load({ basePath: base, type });
    const priority = parseOrderList(el.dataset.sidebarTagsOrder);
    const tags = uniqueValues(items, field, { sort: true, priority });
    if (tags.length === 0) return; // タグが無ければ静的フォールバックのまま
    el.innerHTML = tags
      .map((t) => `<a href="${base}?${encodeURIComponent(field)}=${encodeURIComponent(t)}#list">${this.escape(t)}</a>`)
      .join('');
  }

  /**
   * 読み込む JSON と一覧リンク先を決める。data属性優先、無ければURL先頭セグメント。
   * @param {HTMLElement} el
   * @returns {{base:string, type:string}}
   */
  resolve(el) {
    if (el.dataset.listBase && el.dataset.listType) {
      return { base: el.dataset.listBase, type: el.dataset.listType };
    }
    const seg = window.location.pathname.split('/').filter(Boolean)[0] || '';
    return { base: `/${seg}/`, type: seg };
  }

  escape(s) {
    const div = document.createElement('div');
    div.textContent = s == null ? '' : String(s);
    return div.innerHTML;
  }
}
